/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Button,
  Flex,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  queryClient, useAllAutomations, useAllPlaybooks, useCurrentUser, useTraitsAndMetrics,
} from "../../../../hooks/QueryHooks";
import { createAutomation } from "../../../../services/automation/automationEndpoints";
import { getWorkspaceTrendsAgaintQuery } from "../../../../services/workspace/workspaceEndpoints";
import { ENTITY } from "../../../../services/workspace/workspaceEndpoints.enums";
import useSignalStore from "../../../../store/signal/signalStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import NewSignalBody from "./components/NewSignalBody";
import { getLargerTimeWindow } from "../../../../utils/dateConverter";

const NewSignalPage = () => {
  const {
    signal,
    setSignal,
    setName,
    setAutomationType,
    setEntityRelation,
    setTrigger,
    setMetrics,
    setSelectedFields,
    addFilter,
    addPredicate,
    editPredicate,
    deletePredicate,
    deleteFilter,
    clearFilter,
    clearSignal,
    setStatus,
    setTimeWindow,
    setTimeZone,
    setSuppressionPeriod,
    createAction,
    deleteAction,
    setPlaybook,
    updateAction,
    setResponseData,
    setSort,
  } = useSignalStore();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const [error, setError] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);

  const traitsAndMetrics: any = useTraitsAndMetrics({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: user }: any = useCurrentUser({});

  const { data: playBookData }: any = useAllPlaybooks({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "signal",
    enabled: !!workspace?.id,
  });

  const playbooks = useMemo(() => playBookData?.map((playbook: any) => ({
    label: playbook?.title,
    value: playbook?.id,
  })), [playBookData?.length]);

  const createSignalMutation = useMutation({
    mutationFn: createAutomation,
  });

  const getTotalMutation = useMutation({
    mutationFn: getWorkspaceTrendsAgaintQuery,
  });

  const handleFilterAdd = () => {
    const selectedFilters = signal
      .filters.predicates[signal.filters.predicates.length - 1]?.predicates || [];
    if (selectedFilters.length === 0 || selectedFilters[0].field !== "") {
      addFilter({
        query_type: "AND",
        predicates: [{
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        }],
      });
    }
  };

  const handlePredicateAdd = (filterId?: any) => {
    const selectedFilters = signal.filters.predicates[filterId].predicates;
    if (selectedFilters.length === 0 || selectedFilters[selectedFilters.length - 1].field !== "") {
      addPredicate(
        {
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        },
        filterId,
      );
    }
  };

  const handlePredicateClear = (filterId?: any) => {
    deleteFilter(filterId);
  };

  const handleEntityChange = (value: "accounts" | "users") => {
    if (value === "accounts" && signal.entity_relation !== "accounts") {
      setSelectedFields(["accounts.name", "accounts.last_seen", "accounts.created_at"]);
      setMetrics(["default_metric_total_active_users"]);
      setSort({
        field: "default_metric_total_active_users",
        order: "desc",
        type: "changeval",
      });
    } else if (value === "users" && signal.entity_relation !== "users") {
      setSelectedFields(["users.name", "users.first_name", "users.last_seen", "users.created_at"]);
      setMetrics(["default_metric_days_active"]);
      setSort({
        field: "default_metric_days_active",
        order: "desc",
        type: "changeval",
      });
    }
    setEntityRelation(value);
  };

  const handlePredicateChange = (
    field: string,
    operator: string,
    value: any,
    type: string,
    entity_type?: string,
    id?: any,
    filterId?: any,
    time_window?: string,
  ) => {
    editPredicate({
      field,
      operator,
      value,
      type,
      entity_type: entity_type || "column",
      time_window,
    }, id, filterId);
    const selectedColumns = signal?.selected_fields || [];
    const selectedMetrics = signal?.metrics || [];
    let newColumns: any = selectedColumns;
    const newMetrics: any = (!selectedMetrics.includes(field) && entity_type !== "column" && field !== "") ? [...selectedMetrics, field] : selectedMetrics;
    if (signal?.entity_relation === "accounts" && field.startsWith("accounts.")) {
      newColumns = (!selectedColumns.includes(field) && entity_type === "column" && field !== "") ? [...selectedColumns, field] : selectedColumns;
      setSelectedFields(newColumns);
    } else if (signal?.entity_relation === "users" && field.startsWith("users.")) {
      newColumns = (!selectedColumns.includes(field) && entity_type === "column" && field !== "") ? [...selectedColumns, field] : selectedColumns;
      setSelectedFields(newColumns);
    }
    setMetrics(newMetrics);
    setTimeWindow(getLargerTimeWindow(signal?.time_window, time_window));
  };

  const handlePredicateRemove = (id: number, filterId?: any) => {
    deletePredicate(id, filterId);
  };

  const navigate = useNavigate();

  const onTimeWindowChange = (value: any) => {
    setTimeWindow(value);
  };

  const onSignalSubmit = () => {
    const slackEnabled = signal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_slack");
    const emailEnabled = signal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_email");
    if (!signal?.name || !signal?.entity_relation || !signal?.trigger
      || !signal?.filters?.predicates[0]?.predicates?.length
      || !signal?.automation_actions?.actions?.length
      || !signal?.automation_actions?.suppression_period
      || !signal?.time_window
      || (!!slackEnabled && ((slackEnabled?.message_type === "channel" && !slackEnabled?.channel_id)
        || (slackEnabled?.message_type === "direct" && !slackEnabled?.slack_user_id)))
      || (!!emailEnabled && (!emailEnabled?.to || !emailEnabled?.subject)) || (!signal?.filters?.predicates?.find((fil: any) => fil?.predicates?.find((item: any) => item?.field?.includes("metric"))))) {
      setError(true);
      notify({
        messageList: ["Kindly resolve errors in form fields"],
        type: "warning",
      });
    } else {
      // Fix sort before submission
      let sortConfig = { ...signal.sort };

      // If sort field is empty or invalid, set default based on entity type
      if (!sortConfig.field || sortConfig.field === "") {
        sortConfig = {
          field: signal.entity_relation === "accounts" ? "default_metric_total_active_users" : "default_metric_days_active",
          order: "desc",
          type: "changeval",
        };
      }

      // Ensure sort type is always defined
      if (!sortConfig.type) {
        sortConfig.type = "property";
      }

      // Ensure selected_fields is not empty - use empty array as default
      const selectedFields = signal.selected_fields && signal.selected_fields.length > 0
        ? signal.selected_fields
        : [];

      // Ensure metrics is not empty - use default metric based on entity type
      const metrics = signal.metrics && signal.metrics.length > 0
        ? signal.metrics
        : [signal.entity_relation === "accounts" ? "default_metric_total_active_users" : "default_metric_days_active"];

      // Update sort type for metrics
      if (metrics.length > 0) {
        const isMetricSort = metrics.some((metric: string) => sortConfig.field.includes(metric));
        if (isMetricSort) {
          sortConfig.type = "changeval";
        }
      }

      createSignalMutation.mutate({
        ...signal,
        selected_fields: selectedFields,
        metrics,
        sort: sortConfig,
        time_zone: workspace?.workspace_time_zone,
        status: "active",
        workspace_id: workspace?.id,
        owner_id: user?.user_id,
      }, {
        onSuccess: (data: any) => {
          queryClient.setQueryData(["all_automations", workspace?.id, signal?.automation_type], (oldData: any) => ({
            ...oldData,
            data: [
              ...oldData.data,
              data?.data,
            ],
          }));
          if (process.env.REACT_APP_HE_WORKSPACE_KEY && process.env.REACT_APP_HE_DATA_KEY) {
            window?.hyperengage(
              "track",
              "new_signal_created",
              {
                properties: {
                  name: signal?.name,
                  entity_relation: signal?.entity_relation,
                  trigger: signal?.trigger,
                  actions: signal?.automation_actions?.actions?.map((action: any) => action?.type),
                  time_zone: workspace?.workspace_time_zone,
                  status: "active",
                  workspace_id: workspace?.id,
                  owner_id: user?.user_id,
                },
              },
            );
          }
          notify({
            heading: "New Signal Created",
            type: "success",
            messageList: ["You have successfully created a new Signal."],
          });
          clearSignal();
          navigate(-1);
        },
        onError: (err: any) => {
          if (err.response?.status !== 0) {
            notify({
              heading: "Unknown error.",
              type: "failure",
              messageList: [
                "An unknown error occurred while creating the signal. Please try again later or contact our support team for assistance.",
              ],
            });
          }
        },
      });
    }
  };

  useEffect(
    () => () => { clearSignal(); },
    [],
  );

  return (
    <div className="overflow-auto relative h-full bg-gray-100">
      <div className="max-w-5xl mx-auto pxs-3">
        <NewSignalBody
          clearFilter={clearFilter}
          clearSignal={clearSignal}
          onTimeWindowChange={onTimeWindowChange}
          handlePredicateAdd={handlePredicateAdd}
          handlePredicateClear={handlePredicateClear}
          handlePredicateRemove={handlePredicateRemove}
          handlePredicateChange={handlePredicateChange}
          handleFilterAdd={handleFilterAdd}
          onSignalSubmit={onSignalSubmit}
          error={error}
          total={total}
          traitsAndMetrics={traitsAndMetrics}
          setSelectedMetrics={setMetrics}
          signal={signal}
          setName={setName}
          setEntityRelation={handleEntityChange}
          setTrigger={setTrigger}
          setSuppressionPeriod={setSuppressionPeriod}
          setSelectedFields={setSelectedFields}
          createAction={createAction}
          deleteAction={deleteAction}
          updateAction={updateAction}
          playbooks={playbooks}
          setPlaybook={setPlaybook}
        />
        <Flex justify="flex-end" gap={5} className="mt-2 p-4 bg-gray-100">
          <Button
            variant="outline"
            onClick={() => {
              clearSignal();
              navigate(-1);
            }}
            className="hover:bg-gray-100"
            color="gray"
            disabled={createSignalMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={onSignalSubmit}
            loading={createSignalMutation.isLoading}
          >
            {createSignalMutation.isLoading ? 'Saving...' : 'Save and activate'}
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default NewSignalPage;
